/**
 * lib/reports/narrateSection.ts — LLM writes sentences FROM real numbers
 * ============================================================================
 * Different from a pure template: the LLM is handed the exact computed
 * numbers for a section and asked to phrase a natural sentence using them.
 *
 * Anti-hallucination guarantee, concretely:
 *   1. Every number the LLM is allowed to use is passed in explicitly, as
 *      already-rounded/formatted strings (e.g. "23%", "₹1,064.38").
 *   2. The prompt instructs it to use ONLY these values, verbatim, and to
 *      invent nothing else.
 *   3. After generation, verify() extracts every number-like token from the
 *      LLM's output and checks each one exists in the allowed set. If ANY
 *      token doesn't match, the section falls back to a deterministic
 *      template sentence built from the same numbers — so a bad generation
 *      never reaches the user, it just silently degrades to the safe
 *      template version for that one section.
 */

import { narrateWithFallback } from './llmClient';

export interface NarrationRequest {
  /** Section name, purely for logging. */
  section: string;
  /** Every number/figure the LLM is allowed to reference, pre-formatted exactly as it should appear. */
  allowedValues: Record<string, string>;
  /** Plain-English facts to weave together (already correct, just needs phrasing). */
  facts: string[];
  /** Deterministic fallback sentence, used if the LLM's output fails verification. */
  fallback: string;
  /**
   * Ordered list of steps this section's paragraph MUST follow, one clause/
   * sentence per step. Overrides the generic PF/LF-shaped default structure —
   * use this whenever a section's numbers don't fit "average/low → days below
   * threshold → rebate or surcharge → missed-out delta" (e.g. Contract Demand
   * Utilisation, Week Comparison). Keep each step short and concrete.
   */
  structureHint?: string[];
}

function extractNumberTokens(text: string): string[] {
  // Matches ₹1,064.38 / 23% / 0.912 / 58 / 176 kVA etc.
  const matches = text.match(/₹?-?\d[\d,]*\.?\d*%?/g) ?? [];
  return matches.map(m => m.replace(/,/g, ''));
}

function normalizeNumber(s: string): string {
  return s.replace(/[₹,%\s]/g, '');
}

/**
 * Verifies every number-like token in `text` matches (after normalizing
 * currency symbols/commas/%) some value in `allowedValues`. Returns false
 * on the first unmatched token — strict by design.
 */
function isGrounded(text: string, allowedValues: Record<string, string>): boolean {
  const allowedNormalized = new Set(Object.values(allowedValues).map(normalizeNumber));
  const found = extractNumberTokens(text);
  for (const tok of found) {
    if (!allowedNormalized.has(normalizeNumber(tok))) {
      console.warn(`[narrateSection] Ungrounded number "${tok}" not in allowed set — rejecting generation.`);
      return false;
    }
  }
  return true;
}

const DEFAULT_STRUCTURE = [
  'State the average value this week and the lowest/peak value reached.',
  'If a "days below threshold" style number is given, state it — if that count is 0, write "no days" instead of the digit 0.',
  'State the real rupee outcome this week: was it a rebate/saving earned, or a surcharge/cost paid? Use the FACTS to know which — never guess.',
  'ONLY if a "missed out" / delta-style number is given AND is clearly non-zero, add ONE short final clause naming that amount as what could additionally have been saved or earned — mention it exactly once, never alongside a second hypothetical figure.',
];

function buildSystemPrompt(): string {
  return [
    'You write ONE short, plain-English update (2-3 sentences MAX, no more, no filler) for a factory electricity report section, in the plant\'s own voice (e.g. "Our power factor was...").',
    'You will be given a list of NUMBERS (each with a short description of what it means), a list of FACTS, and a required STRUCTURE (an ordered list of steps).',
    'Follow the STRUCTURE steps in order, one short clause or sentence per step, using ONLY the parts that are relevant — skip a step entirely if its number/fact was not provided. Do not add any extra step, framing, or reasoning beyond what STRUCTURE asks for.',
    'Critical rule: never state the same number twice, and never restate the same idea in two different sentences or add your own "in other words" style restatement.',
    'Critical rule: do not invent extra conceptual framing that was not asked for (e.g. do not talk about "thresholds" for a step that has none, do not say things like "in the sense of overuse", do not use jargon like "cost avoidance" — just state the plain fact).',
    'Rule: you may ONLY use numbers that appear in the NUMBERS list, written exactly as given (same digits, same ₹/%/kVA symbol), except for the "no days" substitution when a count is explicitly 0. Do not calculate, round differently, combine two numbers into a new one, or invent any other number.',
    'Rule: do not add any number, statistic, or figure that is not in the NUMBERS list, even if it seems obvious or safe to infer.',
    'Rule: the description text next to each number (e.g. "average power factor this week") is ONLY there to tell you what the number means — never copy that description, or any code-like/variable-like word (e.g. "thresholdUsed", "daysBelowThreshold"), into your output. Always rephrase everything into your own plain, natural words.',
    'Rule: never use alarmist or exaggerated words like "critical", "severe", "dangerous", "alarming", "urgent", or "serious" — even for a genuinely bad number. State the numbers and the plain physical facts calmly and neutrally; let the reader judge severity from the figures themselves, not from your adjectives.',
    'Be direct and concise — as if explaining to a factory owner with no technical background, in flowing sentences, never as a list. No jargon, no headers, no markdown, no preamble, no repeated ideas — output only the short paragraph.',
  ].join(' ');
}

function buildUserPrompt(req: NarrationRequest): string {
  const values = Object.entries(req.allowedValues).map(([k, v]) => `- ${k}: ${v}`).join('\n');
  const structure = req.structureHint ?? DEFAULT_STRUCTURE;
  return [
    `Section: ${req.section}`,
    `NUMBERS (use ONLY these values, verbatim — the text before the colon just explains what each number is, do not repeat it literally):`,
    values,
    ``,
    `FACTS to weave in (rephrase in your own words, do not copy verbatim):`,
    ...req.facts.map(f => `- ${f}`),
    ``,
    `STRUCTURE (follow these steps in order, one clause/sentence each, skip a step only if it has no matching number/fact):`,
    ...structure.map((s, i) => `${i + 1}. ${s}`),
    ``,
    `Write the paragraph now, in natural flowing prose only — do not number or list the steps, just write connected sentences.`,
  ].join('\n');
}

/**
 * Generates a section's explanation via LLM, grounded strictly in
 * `allowedValues`. Falls back to `fallback` if generation fails OR if the
 * output contains any number not in `allowedValues`.
 */
export async function narrateSection(req: NarrationRequest): Promise<{ text: string; source: 'llm' | 'fallback'; provider?: 'groq' }> {
  try {
    const { text, provider } = await narrateWithFallback(buildSystemPrompt(), buildUserPrompt(req));
    if (isGrounded(text, req.allowedValues)) {
      return { text, source: 'llm', provider };
    }
    return { text: req.fallback, source: 'fallback' };
  } catch (err) {
    console.warn(`[narrateSection] LLM unavailable for "${req.section}", using fallback:`, (err as Error).message);
    return { text: req.fallback, source: 'fallback' };
  }
}
